import { useState, useRef, useEffect } from "react";

interface UseVirtualScrollOptions<T> {
  items: T[];
  itemHeight: number;
  windowSize: number;
}

export function useVirtualScroll<T>({
  items,
  itemHeight,
  windowSize,
}: UseVirtualScrollOptions<T>) {
  const [startIndex, setStartIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const totalItems = items?.length ?? 0;
  const totalHeight = totalItems * itemHeight;

  useEffect(() => {
    if (!items?.length) return;

    const handleScroll = () => {
      if (!listRef.current) return;

      const listOffsetTop = listRef.current.offsetTop;
      const scrollTop = window.scrollY - listOffsetTop;

      if (scrollTop < 0) {
        setStartIndex(0);
        return;
      }
      const newStartIndex = Math.floor(scrollTop / itemHeight);
      const maxStart = Math.max(0, totalItems - windowSize);

      setStartIndex(
        Math.min(Math.max(newStartIndex, 0), maxStart)
      );
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [items, itemHeight, windowSize, totalItems]);

  const visibleItems = items?.slice(
    startIndex,
    startIndex + windowSize
  );

  return {
    listRef,
    visibleItems,
    startIndex,
    totalHeight,
  };
}